import { Divider, Stack } from '@mantine/core';
import type { DataAccessRequestOut, GrantType } from '@repo/api-codegen';
import {
	apiQueryKey,
	useApiIntegrationShowUsers,
	useApproveAccessRequest,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { EntityType } from '@repo/common/enums/entityType';
import { noop } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { queryClient, useIntegration } from '../../../api';
import { ErrorLogs } from '../../ErrorLogs/ErrorLogs';
import { openModal } from '../../ModalManager';
import { ResourceSelector } from '../DataAccessRequestCreateModal/ResourceSelector';
import { DataAccessRequestCredentialsModal } from '../DataAccessRequestCredentialsModal/DataAccessRequestCredentialsModal';
import { CreateAndGrantTemporaryRoleForm } from './Forms/CreateAndGrantTemporaryRoleForm';
import { CreateTemporaryUserAndRoleForm } from './Forms/CreateTemporaryUserAndRoleForm';
import { GrantPrivilegesToRoleForm } from './Forms/GrantPrivilegesToRoleForm';
import { GrantPrivilegesToUserDirectForm } from './Forms/GrantPrivilegesToUserDirectForm';
import { ManualApprovalForm } from './Forms/ManualApprovalForm';
import type { BaseApprovalFormValues } from './types';

export interface DataAccessRequestApproveModalProps {
	onClose: () => void;
	request: DataAccessRequestOut;
	disabled?: boolean;
}

export function DataAccessRequestApproveModal({
	onClose,
	request,
	disabled = false,
}: DataAccessRequestApproveModalProps) {
	const [error, setError] = useState<string | null>(null);

	const { data: integrationData } = useIntegration({
		id: request.integration_id || '',
		options: { enabled: !!request.integration_id },
	});

	const grantType = useMemo<GrantType>(() => {
		// Default or fallback grant type if needed
		let selectedGrantType: GrantType = 'create_and_grant_temporary_role';
		if (integrationData?.access_request_preference) {
			selectedGrantType = integrationData.access_request_preference;
		}
		return selectedGrantType;
	}, [integrationData]);

	const allowedPrivileges = useMemo<string[]>(() => {
		if (integrationData?.access_request_allowed_privileges?.length) {
			return integrationData.access_request_allowed_privileges;
		}
		// Default privileges if none are specified in integration settings
		return ['SELECT', 'INSERT', 'UPDATE', 'DELETE'];
	}, [integrationData]);

	const { data: grantResult, isLoading: isLoadingUsers } =
		useApiIntegrationShowUsers(
			{
				pathParams: {
					integrationId: request.integration_id || '',
				},
			},
			{
				enabled:
					grantType === 'create_and_grant_temporary_role' ||
					grantType === 'grant_privileges_to_user_direct',
				refetchOnMount: false,
				refetchOnWindowFocus: false,
				refetchOnReconnect: false,
			}
		);

	const { mutate: approveAccessRequest, isPending: isLoading } =
		useApproveAccessRequest({
			onSuccess: (response) => {
				queryClient.invalidateQueries({
					queryKey: apiQueryKey('integration_v2/data-access-requests'),
				});
				queryClient.invalidateQueries({
					queryKey: apiQueryKey(
						'integration_v2/data-access-requests/{request_id}',
						{
							request_id: request.id,
						}
					),
				});
				onClose();
				if (response.temporary_user_name && response.temporary_user_password) {
					openModal({
						title: 'View credentials',
						children: <DataAccessRequestCredentialsModal request={response} />,
						size: 'lg',
					});
				}
			},
			onError: (mutationError: unknown) => {
				setError(
					typeof mutationError === 'object' &&
						mutationError !== null &&
						'detail' in mutationError
						? (mutationError.detail as string)
						: String(error)
				);
			},
		});

	const handleSubmit = useCallback(
		async (values: BaseApprovalFormValues, formGrantType: GrantType) => {
			setError(null); // Clear previous errors on new submission
			await approveAccessRequest({
				body: {
					approved_resources: request.requested_resources?.map((resource) => ({
						id: resource.id,
						database_name:
							resource.database_name ??
							(resource.entity_type === EntityType.database
								? resource.title
								: null),
						schema_name:
							resource.schema_name ??
							(resource.entity_type === EntityType.schema
								? resource.title
								: null),
						table_name:
							resource.table_name ??
							(resource.entity_type === EntityType.table
								? resource.title
								: null),
						resource_level: resource.resource_level,
					})),
					approved_expires_at: values.duration
						? values.duration.toISOString()
						: undefined,
					privileges:
						formGrantType !== 'manual' ? values.privileges : undefined,
					approved_text: values.reason,
					username: values.username,
					grant_type: formGrantType,
					role: values.role,
				},
				pathParams: {
					requestId: request.id,
				},
			});

			trackEvent('access_request/approved', {
				grant_type: formGrantType,
				resources_count: String(request.requested_resources?.length ?? 0),
			});
		},
		[approveAccessRequest, request]
	);

	const renderForm = () => {
		const formProps = {
			request,
			integrationData,
			allowedPrivileges,
			onSubmit: handleSubmit,
			onClose,
			isLoading,
			grantResult,
			isLoadingUsers,
			disabled,
		};

		switch (grantType) {
			case 'manual':
				return <ManualApprovalForm {...formProps} />;
			case 'create_and_grant_temporary_role':
				return <CreateAndGrantTemporaryRoleForm {...formProps} />;
			case 'grant_privileges_to_role':
				return <GrantPrivilegesToRoleForm {...formProps} />;
			case 'create_temporary_user_and_role':
				return <CreateTemporaryUserAndRoleForm {...formProps} />;
			case 'grant_privileges_to_user_direct':
				return <GrantPrivilegesToUserDirectForm {...formProps} />;
			default:
				// eslint-disable-next-line no-console
				console.warn(
					'Unknown grant type:',
					grantType,
					'Falling back to manual.'
				);
				return <ManualApprovalForm {...formProps} />;
		}
	};

	return (
		<Stack spacing="md">
			<ResourceSelector
				accessRequestPreference={grantType}
				integrationId={request.integration_id || ''}
				label="Resources"
				name="resources"
				placeholder="Select"
				readOnly
				initialSelectedValues={request.requested_resources ?? []}
				onChange={noop}
				onBlur={noop}
				resourceLevel={
					request.requested_resources?.[0]?.resource_level ?? 'table'
				}
			/>
			<Divider orientation="horizontal" />

			{renderForm()}

			{error && (
				<ErrorLogs title="Approval error" logs={error ? [error] : []} />
			)}
		</Stack>
	);
}
